import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 32, className = "", once = true }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0, as: Tag = "h1" }) => {
  const MTag = motion[Tag] || motion.div;
  return (
    <MTag className={className} data-testid="masked-title">
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.0, delay: delay + i * 0.14, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </MTag>
  );
};

export const RevealInView = ({ lines, className = "", lineClassName = "" }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <motion.span
        key={i}
        className="block overflow-hidden pb-[0.08em] -mb-[0.08em]"
        initial={{ opacity: 0, y: 26 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.75, delay: i * 0.11, ease: EASE }}
      >
        <span className={`block ${lineClassName}`}>{line}</span>
      </motion.span>
    ))}
  </span>
);

export const Counter = ({ value, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  const numeric = parseInt(String(value).replace(/[^0-9]/g, ""), 10) || 0;

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, numeric, {
      duration: 2.0,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, numeric]);

  return (
    <span ref={ref} className={className}>
      {display.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
};

export const SectionHead = ({ overline, title, description, dark = false, align = "left" }) => (
  <div className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}>
    {overline && (
      <Reveal>
        <p className={`text-xs sm:text-sm uppercase tracking-[0.25em] font-bold ${dark ? "text-lightgold" : "text-gold"} mb-4 font-body`} data-testid="section-overline">
          {overline}
        </p>
      </Reveal>
    )}
    <h2 className={`font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-balance pt-8 ${dark ? "text-white" : "text-navy"}`}>
      <RevealInView lines={Array.isArray(title) ? title : [title]} />
    </h2>
    {description && (
      <Reveal delay={0.15}>
        <p className={`mt-5 text-base sm:text-lg leading-relaxed ${dark ? "text-white/65" : "text-muted"}`}>{description}</p>
      </Reveal>
    )}
    <Reveal delay={0.2}>
      <div className={`mt-6 h-px w-24 bg-gradient-to-r from-gold to-lightgold ${align === "center" ? "mx-auto" : ""}`} />
    </Reveal>
  </div>
);

export const PageHero = ({ overline, titleLines, description, image, children }) => (
  <section className="relative min-h-[52vh] flex items-end bg-darknavy noise-bg overflow-hidden" data-testid="page-hero">
    {image && (
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: EASE }}
      >
        <img src={image} alt="" className="w-full h-full object-cover opacity-30" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-b from-darknavy/80 via-darknavy/70 to-darknavy" />
      </motion.div>
    )}
    <div className="absolute top-0 right-0 w-1/3 h-full pointer-events-none">
      <div className="absolute top-24 right-0 w-px h-40 bg-gradient-to-b from-transparent via-gold/40 to-transparent" />
      <div className="absolute top-24 right-16 w-px h-64 bg-gradient-to-b from-transparent via-gold/25 to-transparent" />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-36 pb-16 sm:pb-20 w-full">
      <motion.p
        className="text-xs sm:text-sm uppercase tracking-[0.3em] font-bold text-lightgold mb-5"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
      >
        {overline}
      </motion.p>
      <MaskedLines
        lines={titleLines}
        delay={0.3}
        className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white text-balance"
      />
      {description && (
        <motion.p
          className="mt-6 max-w-2xl text-base sm:text-lg text-white/65 leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
        >
          {description}
        </motion.p>
      )}
      {children}
    </div>
  </section>
);

export const GoldButton = ({ children, className = "", gold = false, ...props }) => (
  <button
    className={`btn-sweep inline-flex items-center justify-center gap-2 font-semibold text-sm tracking-wide px-7 py-3.5 rounded-sm transition-colors duration-300 ${
      gold ? "bg-gold text-darknavy hover:bg-lightgold" : "bg-navy text-white hover:bg-gold hover:text-darknavy"
    } ${className}`}
    {...props}
  >
    {children}
  </button>
);

export const OutlineButton = ({ children, className = "", dark = false, ...props }) => (
  <button
    className={`inline-flex items-center justify-center gap-2 font-semibold text-sm tracking-wide px-7 py-3.5 rounded-sm border transition-colors duration-300 ${
      dark
        ? "border-white/25 text-white hover:border-gold hover:text-lightgold"
        : "border-navy/30 text-navy hover:border-gold hover:text-gold"
    } ${className}`}
    {...props}
  >
    {children}
  </button>
);
